import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { FaPlay } from "react-icons/fa";
import styled from "@emotion/styled";
import {
    CardContainer,
    ThumbnailContainer,
    PlayOverlay,
    CardContent,
    Title,
    Description,
    Badge,
    BadgeContainer,
    MetaDivider,
    MetaList,
    MetaRow,
    MetaLabel,
    MetaValue,
} from "./Clip.elements";

const FixedCardContainer = styled(CardContainer)`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const FixedCardContent = styled(CardContent)`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
`;

const FixedTitle = styled(Title)`
    margin: 0;

    @media (min-width: 1009px) {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
        min-height: calc(1.25em * 3.5);
    }
`;

const DescriptionBox = styled.div`
    position: relative;
    min-height: 0;
`;

const StyledDescription = styled(Description)`
    margin: 0;

    @media (min-width: 1009px) {
        display: ${({ $clamped }) => ($clamped ? "-webkit-box" : "block")};
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${({ $clamped }) => ($clamped ? 3 : "unset")};
        overflow: ${({ $clamped }) => ($clamped ? "hidden" : "visible")};
        min-height: ${({ $clamped }) => ($clamped ? "calc(1.5em * 3)" : "unset")};
    }
`;

const MeasureDescription = styled(Description)`
    position: absolute;
    inset: 0;
    visibility: hidden;
    pointer-events: none;
    z-index: -1;
    height: auto;
    overflow: visible;
    display: block;
    margin: 0;
`;

const ReadMoreButton = styled.button`
    display: none;

    @media (min-width: 1009px) {
        display: inline-flex;
        align-items: center;
        margin-top: 6px;
        padding: 0;
        border: none;
        background: transparent;
        color: #8f7ee7;
        font-size: 12px;
        font-weight: 800;
        cursor: pointer;

        &:hover {
            color: #a899f5;
        }
    }
`;

const BottomMeta = styled.div`
  margin-top: auto;
`;

const ClipCard = ({ file }) => {
    const [playing, setPlaying] = useState(false);
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const [video, setVideo] = useState(null);
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const [shouldClamp, setShouldClamp] = useState(false);
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 1008);

    const measureRef = useRef(null);

    useLayoutEffect(() => {
        if (!video) return undefined;

        const handlePlay = () => {
            video.pause();
        };

        video.addEventListener("play", handlePlay);

        return () => {
            video.removeEventListener("play", handlePlay);
        };
    }, [video]);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth > 1008);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useLayoutEffect(() => {
        if (!isDesktop) {
            setShouldClamp(false);
            setDescriptionExpanded(false);
            return;
        }

        const measureNode = measureRef.current;
        if (!measureNode) return;

        const styles = window.getComputedStyle(measureNode);
        const lineHeight = parseFloat(styles.lineHeight);
        if (!lineHeight) {
            setShouldClamp(false);
            return;
        }

        const maxHeight = lineHeight * 3;
        const fullHeight = measureNode.scrollHeight;

        setShouldClamp(fullHeight > maxHeight + 1);
    }, [file.description, isDesktop]);

    const handlePlayClick = () => {
        setPlaying(true);
    };

    const resolutionLabels = {
        FOUR_K: "4K",
        HD_PLUS: "HD+",
        HD: "<HD",
    };

    const formattedDate = file.processedDate
        ? new Date(file.processedDate * 1000).toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
        })
        : "";

    const isClamped = isDesktop && shouldClamp && !descriptionExpanded;

    return (
        <FixedCardContainer>
            <ThumbnailContainer onClick={handlePlayClick}>
                {!playing ? (
                    <>
                        <video
                            ref={setVideo}
                            src={file.videoLink}
                            onLoadedData={() => setPreviewLoaded(true)}
                            style={{
                                display: previewLoaded ? "block" : "none",
                                width: "100%",
                            }}
                            muted
                            loop
                            autoPlay
                            playsInline
                        />

                        <PlayOverlay>
                            <FaPlay color="#fff" size={20} />
                        </PlayOverlay>

                        <BadgeContainer>
                            <Badge type={file.resolution}>
                                {resolutionLabels[file.resolution]}
                            </Badge>
                        </BadgeContainer>
                    </>
                ) : (
                    <>
                        <BadgeContainer>
                            <Badge type={file.resolution}>
                                {resolutionLabels[file.resolution]}
                            </Badge>
                        </BadgeContainer>

                        <ReactPlayer
                            url={file.videoLink}
                            playing={playing}
                            controls
                            width="100%"
                            height="100%"
                        />
                    </>
                )}
            </ThumbnailContainer>

            <FixedCardContent>
                <FixedTitle title={file.headline}>{file.headline}</FixedTitle>

                <DescriptionBox>
                    <StyledDescription $clamped={isClamped}>
                        {file.description}
                    </StyledDescription>

                    <MeasureDescription ref={measureRef}>
                        {file.description}
                    </MeasureDescription>

                    {shouldClamp && (
                        <ReadMoreButton
                            type="button"
                            onClick={() => setDescriptionExpanded((prev) => !prev)}
                        >
                            {descriptionExpanded ? "Read less" : "Read more"}
                        </ReadMoreButton>
                    )}
                </DescriptionBox>

                {(file.fileName || file.processedDate) && (
                    <BottomMeta>
                        <MetaDivider />

                        <MetaList>
                            {file.fileName && (
                                <MetaRow>
                                    <MetaLabel>File name:</MetaLabel>
                                    <MetaValue title={file.fileName}>{file.fileName}</MetaValue>
                                </MetaRow>
                            )}

                            {file.processedDate && (
                                <MetaRow>
                                    <MetaLabel>Processed:</MetaLabel>
                                    <MetaValue title={formattedDate}>{formattedDate}</MetaValue>
                                </MetaRow>
                            )}
                        </MetaList>
                    </BottomMeta>
                )}
            </FixedCardContent>
        </FixedCardContainer>
    );
};

export default ClipCard;